import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  FolderIcon,
  ChartBarIcon,
  LightBulbIcon,
  DocumentTextIcon,
  ListBulletIcon,
  CalendarDaysIcon,
  ClockIcon,
  ShieldCheckIcon,
  PresentationChartLineIcon,
  DocumentIcon,
  TableCellsIcon,
  ArchiveBoxIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { AppLogo } from '../ui/AppLogo';
import { Badge } from '../ui/badge';
import { useGlobalProject } from '../../contexts/GlobalProjectContext';
import usePermission from '../../hooks/usePermission';
import type { Permission } from '../../hooks/usePermission';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

// Permission required for each nav area (single source of truth; align with route guards)
const NAV_PERMISSIONS = {
  home: 'view_projects' as Permission,
  projects: 'view_projects' as Permission,
  osLayer: 'view_projects' as Permission,
  activities: 'view_projects' as Permission,
  gantt: 'view_schedules' as Permission,
  reports: 'view_reports' as Permission,
  insights: 'view_insights' as Permission,
  analytics: 'view_analytics' as Permission,
  explorer: 'view_projects' as Permission,
} as const;

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useGlobalProject();
  const { hasPermission } = usePermission();

  const osLayerPages = [
    { name: 'Timeline of Truth', icon: ClockIcon, path: 'timeline' },
    { name: 'Change Intelligence', icon: ChartBarIcon, path: 'change-intelligence' },
    { name: 'Compile Gate', icon: ShieldCheckIcon, path: 'compile-gate' },
    { name: 'Forecast', icon: PresentationChartLineIcon, path: 'forecast' },
    { name: 'Decision Pack', icon: DocumentIcon, path: 'decision-pack' },
  ];

  const applicationItems = [
    { name: 'Activities', icon: ListBulletIcon, path: '/app/activities' },
    { name: 'Reports', icon: DocumentTextIcon, path: '/app/reports' },
    { name: 'Data Explorer', icon: TableCellsIcon, path: '/app/explorer' },
    // Analytics is rendered as a collapsible group, not here
  ];

  const inDevelopmentNavItems = [
    { name: 'Gantt Chart', icon: CalendarDaysIcon, path: '/app/gantt' },
    { name: 'Insights', icon: LightBulbIcon, path: '/app/insights' },
  ];

  const analyticsSubItems = [
    { name: 'Overview', path: '/app/analytics/overview' },
    { name: 'Library', path: '/app/analytics/catalog' },
    { name: 'Health', path: '/app/analytics/health' },
    { name: 'Critical Path', path: '/app/analytics/critical-path' },
    { name: 'Milestones', path: '/app/analytics/milestones' },
    { name: 'Quality', path: '/app/analytics/quality' },
  ];

  // Auto-expand analytics group when user is on any analytics page
  const isOnAnalytics = location.pathname.startsWith('/app/analytics');
  const [analyticsExpanded, setAnalyticsExpanded] = useState(isOnAnalytics);

  // Keep group expanded while user navigates within analytics
  React.useEffect(() => {
    if (isOnAnalytics && !analyticsExpanded) {
      setAnalyticsExpanded(true);
    }
  }, [isOnAnalytics]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Theme-aware styling (sidebar tokens for container; nb-* for nav items for legacy consistency)
  const textPrimary = 'text-nb-text-secondary';
  const textSecondary = 'text-nb-text-muted';
  const hoverBg = 'hover:bg-nb-surface-2';
  const activeBg = 'bg-nb-surface-2';
  const activeBorder = 'border-l-nb-accent';
  
  // Console-density navigation sizing
  const navItemPadding = collapsed ? 'p-2' : 'px-2.5 py-1.5';
  const navItemTextSize = 'text-xs';
  const navIconSize = 'h-3.5 w-3.5';

  const isProduction = import.meta.env.PROD;

  return (
    <div
      className={`${collapsed ? 'w-16' : 'w-64'} bg-sidebar border-sidebar-border border-r flex flex-col flex-shrink-0 transition-all duration-200`}
    >
      {/* Logo - Minimal */}
      <div
        className={`${collapsed ? 'h-11' : 'h-11'} border-sidebar-border border-b flex items-center ${collapsed ? 'justify-center px-3' : 'px-3'}`}
      >
        {collapsed ? (
          <AppLogo size="small" showWordmark={false} />
        ) : (
          <div className="flex items-center space-x-2.5 w-full">
            <AppLogo size="small" showWordmark={false} />
            <div className="flex flex-col flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold leading-tight ${textPrimary}`}>Edge Technologies</span>
                <Badge variant="outline" size="sm" className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-muted-foreground/50 shrink-0">
                  Beta
                </Badge>
              </div>
              <span className={`text-[10px] leading-tight ${textSecondary}`}>Project Intelligence Platform</span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation - Console style: flat, tight */}
      <nav className="flex-1 overflow-y-auto py-1.5" aria-label="Primary">
        {/* Primary Navigation */}
        <div className="space-y-0.5 px-1.5">
          {hasPermission(NAV_PERMISSIONS.home) && (
            <NavItem
              icon={HomeIcon}
              label="Home"
              active={location.pathname === '/'}
              collapsed={collapsed}
              onClick={() => navigate('/')}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              hoverBg={hoverBg}
              activeBg={activeBg}
              activeBorder={activeBorder}
              navItemPadding={navItemPadding}
              navItemTextSize={navItemTextSize}
              navIconSize={navIconSize}
            />
          )}
          {hasPermission(NAV_PERMISSIONS.projects) && (
            <NavItem
              icon={FolderIcon}
              label="Projects"
              active={isActive('/app/projects')}
              collapsed={collapsed}
              onClick={() => navigate('/app/projects')}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              hoverBg={hoverBg}
              activeBg={activeBg}
              activeBorder={activeBorder}
              navItemPadding={navItemPadding}
              navItemTextSize={navItemTextSize}
              navIconSize={navIconSize}
            />
          )}
          {hasPermission(NAV_PERMISSIONS.projects) && (
            <NavItem
              icon={ArchiveBoxIcon}
              label="Baselines"
              active={isActive('/app/baselines')}
              collapsed={collapsed}
              onClick={() => navigate('/app/baselines')}
              textPrimary={textPrimary}
              textSecondary={textSecondary}
              hoverBg={hoverBg}
              activeBg={activeBg}
              activeBorder={activeBorder}
              navItemPadding={navItemPadding}
              navItemTextSize={navItemTextSize}
              navIconSize={navIconSize}
            />
          )}
        </div>

        {/* Application Layer - each item gated by permission */}
        {!collapsed && (
          <>
            <div className={`px-3 py-1.5 mt-3 border-sidebar-border border-t`}>
              <div className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>
                APPLICATION
              </div>
            </div>
            <div className="space-y-0.5 px-1.5">
              {applicationItems.map((item) => {
                const perm = item.path === '/app/reports' ? NAV_PERMISSIONS.reports
                  : item.path === '/app/activities' ? NAV_PERMISSIONS.activities
                  : item.path === '/app/explorer' ? NAV_PERMISSIONS.explorer
                  : NAV_PERMISSIONS.projects;
                if (!hasPermission(perm)) return null;
                const active = isActive(item.path);
                return (
                  <NavItem
                    key={item.path}
                    icon={item.icon}
                    label={item.name}
                    active={active}
                    collapsed={collapsed}
                    onClick={() => navigate(item.path)}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                    hoverBg={hoverBg}
                    activeBg={activeBg}
                    activeBorder={activeBorder}
                    navItemPadding={navItemPadding}
                    navItemTextSize={navItemTextSize}
                    navIconSize={navIconSize}
                  />
                );
              })}

              {/* Analytics: navigate (label) vs expand/collapse (chevron only) */}
              {hasPermission(NAV_PERMISSIONS.analytics) && (
                <div>
                  <div
                    className={`flex w-full items-stretch rounded transition-colors gap-0.5 ${
                      isOnAnalytics ? `${activeBg} ${activeBorder} border-l-2` : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setAnalyticsExpanded(true);
                        navigate('/app/analytics/overview');
                      }}
                      className={`flex flex-1 min-w-0 items-center ${navItemPadding} ${navItemTextSize} rounded transition-colors text-left ${
                        isOnAnalytics ? textPrimary : `${textSecondary} ${hoverBg}`
                      }`}
                      aria-current={location.pathname === '/app/analytics/overview' ? 'page' : undefined}
                    >
                      <ChartBarIcon className={`${navIconSize} mr-2.5 shrink-0`} />
                      <span className="flex-1 truncate">Analytics</span>
                    </button>
                    <button
                      type="button"
                      className={`flex shrink-0 items-center justify-center px-1 rounded transition-colors ${textSecondary} ${hoverBg}`}
                      aria-expanded={analyticsExpanded}
                      aria-controls="sidebar-analytics-submenu"
                      aria-label={analyticsExpanded ? 'Collapse Analytics links' : 'Expand Analytics links'}
                      onClick={() => setAnalyticsExpanded((v) => !v)}
                    >
                      {analyticsExpanded ? (
                        <ChevronDownIcon className={`${navIconSize} opacity-70`} aria-hidden />
                      ) : (
                        <ChevronRightIcon className={`${navIconSize} opacity-70`} aria-hidden />
                      )}
                    </button>
                  </div>
                  {analyticsExpanded && (
                    <div
                      id="sidebar-analytics-submenu"
                      className="ml-3.5 pl-2.5 border-l border-sidebar-border space-y-0.5 mt-0.5"
                    >
                      {analyticsSubItems.map((sub) => {
                        const subActive = location.pathname === sub.path;
                        return (
                          <button
                            key={sub.path}
                            type="button"
                            onClick={() => navigate(sub.path)}
                            aria-current={subActive ? 'page' : undefined}
                            className={`w-full text-left ${navItemPadding} ${navItemTextSize} rounded transition-colors ${
                              subActive
                                ? `${activeBg} ${textPrimary}`
                                : `${textSecondary} ${hoverBg}`
                            }`}
                          >
                            {sub.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* IN DEVELOPMENT - Gantt, Insights, and project-state pages */}
        {!collapsed && (hasPermission(NAV_PERMISSIONS.gantt) || hasPermission(NAV_PERMISSIONS.insights) || hasPermission(NAV_PERMISSIONS.osLayer)) && (
          <>
            <div className={`px-3 py-1.5 mt-3 border-sidebar-border border-t`}>
              <div className={`text-[10px] uppercase tracking-wider ${textSecondary}`}>
                IN DEVELOPMENT
              </div>
            </div>
            <div className="space-y-0.5 px-1.5">
              {/* Gantt Chart and Insights (no project required) */}
              {inDevelopmentNavItems.map((item) => {
                const perm = item.path === '/app/gantt' ? NAV_PERMISSIONS.gantt : NAV_PERMISSIONS.insights;
                if (!hasPermission(perm)) return null;
                const active = item.path === '/app/gantt' ? isActive('/app/gantt') : isActive('/app/insights');
                const PageIcon = item.icon;
                const disabled = isProduction;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => { if (!disabled) navigate(item.path); }}
                    disabled={disabled}
                    title={disabled ? 'Available in development only' : item.name}
                    aria-current={active && !disabled ? 'page' : undefined}
                    className={`w-full flex items-center ${navItemPadding} ${navItemTextSize} rounded transition-colors ${
                      disabled
                        ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                        : active
                          ? `${activeBg} ${textPrimary} ${activeBorder} border-l-2`
                          : `${textSecondary} ${hoverBg}`
                    }`}
                  >
                    <PageIcon className={`${navIconSize} mr-2.5`} />
                    {item.name}
                  </button>
                );
              })}
              {/* Project-scoped OS layer pages (require selected project) */}
              {hasPermission(NAV_PERMISSIONS.osLayer) && selectedProject && (
                osLayerPages.map((page) => {
                  const PageIcon = page.icon;
                  const pagePath = `/app/project-state/${selectedProject.id}/${page.path}`;
                  const pageActive = location.pathname === pagePath;
                  const pageDisabled = isProduction;
                  return (
                    <button
                      key={page.path}
                      type="button"
                      onClick={() => { if (!pageDisabled) navigate(pagePath); }}
                      disabled={pageDisabled}
                      title={pageDisabled ? 'Available in development only' : page.name}
                      aria-current={pageActive && !pageDisabled ? 'page' : undefined}
                      className={`w-full flex items-center ${navItemPadding} ${navItemTextSize} rounded transition-colors ${
                        pageDisabled
                          ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                          : pageActive
                            ? `${activeBg} ${textPrimary} ${activeBorder} border-l-2`
                            : `${textSecondary} ${hoverBg}`
                      }`}
                    >
                      <PageIcon className={`${navIconSize} mr-2.5`} />
                      {page.name}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Collapsed: same IA order as expanded — APPLICATION, then IN DEVELOPMENT */}
        {collapsed && (
          <div className="space-y-0.5 px-2 mt-2 border-sidebar-border border-t pt-2">
            {applicationItems.map((item) => {
              const perm = item.path === '/app/reports' ? NAV_PERMISSIONS.reports
                : item.path === '/app/activities' ? NAV_PERMISSIONS.activities
                : item.path === '/app/explorer' ? NAV_PERMISSIONS.explorer
                : NAV_PERMISSIONS.projects;
              if (!hasPermission(perm)) return null;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center justify-center ${navItemPadding} rounded transition-colors ${
                    active
                      ? `${activeBg} ${textPrimary}`
                      : `${textSecondary} ${hoverBg}`
                  }`}
                  title={item.name}
                  aria-current={active ? 'page' : undefined}
                >
                  <item.icon className={navIconSize} />
                </button>
              );
            })}
            {hasPermission(NAV_PERMISSIONS.analytics) && (
              <button
                type="button"
                onClick={() => navigate('/app/analytics/overview')}
                className={`w-full flex items-center justify-center ${navItemPadding} rounded transition-colors ${
                  isOnAnalytics
                    ? `${activeBg} ${textPrimary}`
                    : `${textSecondary} ${hoverBg}`
                }`}
                title="Analytics"
                aria-current={location.pathname === '/app/analytics/overview' ? 'page' : undefined}
              >
                <ChartBarIcon className={navIconSize} />
              </button>
            )}
            {inDevelopmentNavItems.map((item) => {
              const perm = item.path === '/app/gantt' ? NAV_PERMISSIONS.gantt : NAV_PERMISSIONS.insights;
              if (!hasPermission(perm)) return null;
              const active = isActive(item.path);
              const PageIcon = item.icon;
              const disabled = isProduction;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => { if (!disabled) navigate(item.path); }}
                  disabled={disabled}
                  title={disabled ? 'Available in development only' : item.name}
                  aria-current={active && !disabled ? 'page' : undefined}
                  className={`w-full flex items-center justify-center ${navItemPadding} rounded transition-colors ${
                    disabled
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                      : active ? `${activeBg} ${textPrimary}` : `${textSecondary} ${hoverBg}`
                  }`}
                >
                  <PageIcon className={navIconSize} />
                </button>
              );
            })}
            {hasPermission(NAV_PERMISSIONS.osLayer) && selectedProject && osLayerPages.map((page) => {
              const PageIcon = page.icon;
              const pagePath = `/app/project-state/${selectedProject.id}/${page.path}`;
              const pageActive = location.pathname === pagePath;
              const pageDisabled = isProduction;
              return (
                <button
                  key={page.path}
                  type="button"
                  onClick={() => { if (!pageDisabled) navigate(pagePath); }}
                  disabled={pageDisabled}
                  title={pageDisabled ? 'Available in development only' : page.name}
                  aria-current={pageActive && !pageDisabled ? 'page' : undefined}
                  className={`w-full flex items-center justify-center ${navItemPadding} rounded transition-colors ${
                    pageDisabled
                      ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                      : pageActive
                        ? `${activeBg} ${textPrimary}`
                        : `${textSecondary} ${hoverBg}`
                  }`}
                >
                  <PageIcon className={navIconSize} />
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom Controls */}
      <div className="border-sidebar-border border-t py-1.5">
        <div className="space-y-0.5 px-1.5">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : navItemPadding} ${navItemTextSize} rounded transition-colors ${textSecondary} ${hoverBg}`}
          >
            <svg
              className={navIconSize}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={collapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7M19 19l-7-7 7-7"}
              />
            </svg>
            {!collapsed && <span className="ml-2.5">Collapse</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

interface NavItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Muted suffix shown after the label, e.g. "Experimental" */
  tag?: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
  textPrimary: string;
  textSecondary: string;
  hoverBg: string;
  activeBg: string;
  activeBorder: string;
  navItemPadding: string;
  navItemTextSize: string;
  navIconSize: string;
  trailingIcon?: React.ComponentType<{ className?: string }>;
}

const NavItem: React.FC<NavItemProps> = ({
  icon: Icon,
  label,
  tag,
  active,
  collapsed,
  onClick,
  textPrimary,
  textSecondary,
  hoverBg,
  activeBg,
  activeBorder,
  navItemPadding,
  navItemTextSize,
  navIconSize,
  trailingIcon: TrailingIcon,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`w-full flex items-center ${collapsed ? 'justify-center' : navItemPadding} ${navItemTextSize} rounded transition-colors ${
        active
          ? `${activeBg} ${textPrimary} ${activeBorder} border-l-2`
          : `${textSecondary} ${hoverBg}`
      }`}
      title={collapsed ? (tag ? `${label} (${tag})` : label) : undefined}
    >
      <Icon className={`${navIconSize} ${collapsed ? '' : 'mr-2.5'}`} />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">
            {label}
            {tag && <span className="ml-1 opacity-50 text-[10px]">({tag})</span>}
          </span>
          {TrailingIcon && <TrailingIcon className={`${navIconSize} ml-1 opacity-70`} />}
        </>
      )}
    </button>
  );
};

export default Sidebar;
